import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import React, { useCallback, useMemo, useState } from 'react';

import { JobFormRow } from './JobFormRow';
import { JobInteraction } from './useListJobActions';
import { ActionPermissions, CustomPermission, FormObject } from '../../@types';
import { HttpMethod } from '../../api/http-client';
import { submitFormsDataWithOriginalPayload } from '../../api/interactions';
import {
  InteractionFormDensity,
  InteractionFormShell,
} from '../../components/molecules/InteractionFormShell';
import { isUserHasPermission } from '../../configs/permissions';

export interface InteractionDialogProps {
  interactions: JobInteraction[];
  permissions?: CustomPermission[];
  onComplete: () => void;
  onCancel: () => void;
}

/**
 * Shows interaction forms that were triggered during a status change.
 * Groups forms by job so the user can see which forms belong to which job.
 */
export const InteractionDialog: React.FC<InteractionDialogProps> = ({
  interactions,
  permissions,
  onComplete,
  onCancel,
}) => {
  const open = interactions.length > 0;
  const [formState, setFormState] = useState<Map<number, FormObject[]>>(new Map());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Tracks jobs that have already submitted successfully. On Submit retry we
  // skip these so non-idempotent POSTs (part-action bulkSubmit) aren't replayed
  // and duplicated.
  const [succeededJobs, setSucceededJobs] = useState<Set<number>>(new Set());

  // Initialize form state when interactions change
  React.useEffect(() => {
    if (interactions.length > 0) {
      const map = new Map<number, FormObject[]>();
      for (const job of interactions) map.set(job.serviceId, [...job.forms]);
      setFormState(map);
      setError('');
      setSucceededJobs(new Set());
    }
  }, [interactions]);

  const canBypass = isUserHasPermission(
    ActionPermissions.Configure_Interactions_Bypass_Mandatory,
    permissions
  );

  const totalForms = useMemo(
    () => interactions.reduce((sum, j) => sum + j.forms.length, 0),
    [interactions]
  );
  const mandatoryCount = useMemo(
    () => interactions.reduce((sum, j) => sum + j.forms.filter((f) => f.isMandatory).length, 0),
    [interactions]
  );

  const updateForm = useCallback((serviceId: number, idx: number, form: FormObject) => {
    setFormState((prev) => {
      const next = new Map(prev);
      const forms = [...(next.get(serviceId) ?? [])];
      forms[idx] = form;
      next.set(serviceId, forms);
      return next;
    });
    setError('');
  }, []);

  const handleSubmit = useCallback(async () => {
    setError('');
    setSubmitting(true);
    const committed = new Set(succeededJobs);
    try {
      for (const job of interactions) {
        if (committed.has(job.serviceId)) continue;
        const forms = formState.get(job.serviceId) ?? job.forms;
        await submitFormsDataWithOriginalPayload(job.originalUrl, job.method ?? HttpMethod.Put, {
          ...job.originalPayload,
          byPassInteractionCheck: true,
          interactionForms: forms,
          actionTriggers: [],
        });
        committed.add(job.serviceId);
      }
      setFormState(new Map());
      setSucceededJobs(new Set());
      onComplete();
    } catch {
      // Persist partial progress so the next Submit only retries the failed tail.
      setSucceededJobs(committed);
      setError('Failed to submit interaction forms');
    } finally {
      setSubmitting(false);
    }
  }, [interactions, formState, succeededJobs, onComplete]);

  const handleSkip = useCallback(() => {
    if (!canBypass && mandatoryCount > 0) {
      setError('You do not have permission to skip mandatory interactions');
      return;
    }
    setFormState(new Map());
    onComplete();
  }, [canBypass, mandatoryCount, onComplete]);

  if (!open) return null;

  const isBulk = interactions.length > 1;
  const title = isBulk
    ? `Interaction Forms — ${interactions.length} jobs`
    : `Interaction Forms — Job #${interactions[0].serviceId}`;

  // Backdrop/escape dismissal would leave the originating action in limbo —
  // the BFF probe has already registered the pending interactions, so the user
  // must explicitly Cancel (clear + close) or Submit.
  const handleDialogClose = (_: unknown, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
      aria-label={title}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {title}
        {mandatoryCount > 0 && (
          <Chip
            label={`${mandatoryCount} mandatory`}
            size="small"
            sx={{ height: 20, fontSize: '10px', bgcolor: 'warning.main', color: 'common.white' }}
          />
        )}
        <Typography sx={{ fontSize: '12px', color: 'text.secondary', ml: 'auto' }}>
          {totalForms} form{totalForms !== 1 && 's'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {interactions.map((job) => {
          const forms = formState.get(job.serviceId) ?? job.forms;
          return (
            <Box key={job.serviceId} sx={{ mb: 2 }}>
              {isBulk && (
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 700,
                    mb: 1,
                    pb: 0.5,
                    borderBottom: '1px solid',
                    borderColor: 'v4.border',
                  }}
                >
                  Job #{job.serviceId}
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {forms.map((form, idx) => (
                  <InteractionFormShell
                    key={form.id ?? idx}
                    title={form.title}
                    isMandatory={form.isMandatory}
                    density={InteractionFormDensity.Compact}
                  >
                    <JobFormRow
                      serviceId={job.serviceId}
                      form={form}
                      forms={forms}
                      onChange={(updated) => updateForm(job.serviceId, idx, updated)}
                    />
                  </InteractionFormShell>
                ))}
              </Box>
            </Box>
          );
        })}
        {error && (
          <Typography sx={{ fontSize: '12px', color: 'error.main', mt: 1 }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        {(canBypass || mandatoryCount === 0) && (
          <Button onClick={handleSkip} disabled={submitting} sx={{ color: 'text.secondary' }}>
            Skip
          </Button>
        )}
        <Button variant="outlined" color="error" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{ bgcolor: 'v4.accent', '&:hover': { bgcolor: 'v4.accent', opacity: 0.9 } }}
        >
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
